<script setup>
/**
 * One WhatsApp channel, and what is actually happening on it.
 *
 * THIS ROW WON THE MERGE, AND `InboxStatusRow.vue` IS GONE. The two used to split the job:
 * the Overview's asked "is anything wrong here", where a chip is the whole answer, and this
 * one lived on the Channels page an owner opened because something IS wrong — so every
 * state here carries the consequence and, where there is one, the thing to do about it.
 * "Contact us and we will put your assistant on this channel" was the entire reason the
 * disconnected state needed a page of its own.
 *
 * When Channels was merged into Overview the cheap direction was to keep the chip and drop
 * this row. That was the wrong way round: the thin row was only ever defensible BECAUSE a
 * richer page existed elsewhere, and deleting that page removed the justification along
 * with it. What an owner needs when a channel is not connected is the consequence and the
 * thing to do about it, and a chip has room for neither.
 *
 * This row also takes a TRI-STATE and says "Set up" when the answer is genuinely unknown.
 * Be precise about that one: on the old page the config was a SECONDARY read that could
 * fail alone, so an owner really saw it. On Overview it is the PRIMARY read and a failure
 * takes the whole page to an error state, so `'unknown'` cannot currently paint there. It
 * stays because it is the honest default for this contract — not as a claim about a state
 * Overview can show today.
 */
import { computed } from 'vue';

import { useWasloI18n } from '../i18n.js';
import { STATUS_STATES, statusCopyKey, toneOf } from '../helpers/status.js';

const props = defineProps({
  /** One entry from `wasloApi.getInboxes`. */
  inbox: { type: Object, required: true },
  /**
   * LEGACY: read only when the BFF sent no `state` for this channel (one version behind).
   * The workspace-wide reply switch: `'on'`, `'off'`, or `'unknown'` when that read failed.
   * There is no per-channel version of it in the sidecar, so this row offers no control —
   * a switch here would be one that writes nowhere.
   */
  replies: {
    type: String,
    default: 'unknown',
    validator: value => ['on', 'off', 'unknown'].includes(value),
  },
});

const { t } = useWasloI18n();

const key = name => `WASLO.OVERVIEW.CHANNELS.STATE.${name}`;

/** P-5.1: the chip and body for a server state, by colour family. */
const TONES = {
  on: { chip: 'text-n-blue-11 bg-n-blue-3', dot: 'bg-n-blue-9' },
  practice: { chip: 'text-n-amber-11 bg-n-amber-3', dot: 'bg-n-amber-9' },
  stopped: { chip: 'text-n-ruby-11 bg-n-ruby-3', dot: 'bg-n-ruby-9' },
  off: { chip: 'text-n-slate-11 bg-n-alpha-2', dot: 'bg-n-slate-8' },
};

/**
 * The channel's name, or "Channel 9" when her Chatwoot session could not name it (the BFF
 * still lists every bot-backed channel then, rather than "we couldn't list your channels").
 */
const channelName = computed(
  () =>
    props.inbox.name ||
    t('WASLO.STATUS.CHANNEL_NAMELESS', { id: props.inbox.id })
);

const state = computed(() => {
  // THE SERVER'S STATE, when the BFF sent one: the same words as the headline above.
  if (STATUS_STATES.includes(props.inbox.state)) {
    const copy = statusCopyKey(props.inbox.state);
    return {
      label: t(`${copy}.CHIP`),
      body: t(`${copy}.BODY`),
      ...TONES[toneOf(props.inbox.state)],
    };
  }
  if (!props.inbox.connected) {
    return {
      label: t(key('NOT_CONNECTED')),
      body: t(key('NOT_CONNECTED_BODY')),
      chip: 'text-n-slate-11 bg-n-alpha-2',
      dot: 'bg-n-slate-8',
    };
  }
  if (props.replies === 'off') {
    return {
      label: t(key('PAUSED')),
      body: t(key('PAUSED_BODY')),
      chip: 'text-n-amber-11 bg-n-amber-3',
      dot: 'bg-n-amber-9',
    };
  }
  if (props.replies === 'unknown') {
    return {
      label: t(key('READY')),
      body: t(key('READY_BODY')),
      chip: 'text-n-slate-11 bg-n-alpha-2',
      dot: 'bg-n-slate-8',
    };
  }
  return {
    label: t(key('ANSWERING')),
    body: t(key('ANSWERING_BODY')),
    // `n-blue-*` is the accent: the branding overlay remaps it emerald in BOTH themes,
    // which is what `n-brand` utilities silently fail to do (THE PLAN, §5 R3).
    chip: 'text-n-blue-11 bg-n-blue-3',
    dot: 'bg-n-blue-9',
  };
});
</script>

<template>
  <li
    class="flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-start"
    :data-waslo-channel="inbox.id"
  >
    <span
      class="grid rounded-lg shrink-0 place-items-center size-9 bg-n-alpha-2"
      aria-hidden="true"
    >
      <span class="size-4 i-lucide-message-square text-n-slate-11" />
    </span>

    <div class="flex flex-col min-w-0 gap-1">
      <!--
        A `<bdi>` INSIDE A PLAIN PARAGRAPH, not `dir="auto"` ON the paragraph — and the
        difference was visible in the very first screenshot of this page. `dir="auto"`
        makes the whole BLOCK right-to-left for an Arabic name, so «فرع السالمية — واتساب»
        flew to the far edge of a 700px row while the number and the sentence under it
        stayed at the other one, and that row alone looked broken. `<bdi>` is an inline
        isolate carrying `dir="auto"` of its own: the name is laid out right-to-left inside
        its own box, and the box sits at the column's inline start with every other row.
        `helpers/bidi.js` writes the long version.
      -->
      <p
        class="mb-0 text-sm font-medium break-words text-n-slate-12"
        data-waslo-channel-name
      >
        <bdi>{{ channelName }}</bdi>
      </p>
      <!-- A phone number is read left to right in every locale. -->
      <p class="mb-0 text-sm text-n-slate-10" dir="ltr">
        {{ inbox.phoneNumber || t('WASLO.OVERVIEW.CHANNELS.NO_NUMBER') }}
      </p>
      <p class="mb-0 text-sm leading-6 max-w-[62ch] text-n-slate-11">
        {{ state.body }}
      </p>
    </div>

    <span
      class="inline-flex items-center h-6 gap-2 px-2 text-xs font-medium rounded-md shrink-0 sm:ms-auto ms-0"
      :class="state.chip"
      data-waslo-channel-state
    >
      <span
        class="rounded-full size-1.5"
        :class="state.dot"
        aria-hidden="true"
      />
      {{ state.label }}
    </span>
  </li>
</template>
